import { useEffect } from 'react';

const SITE_NAME = 'Buy In Burlington';
const BASE_URL = 'https://buyinburlington.ca';
const DEFAULT_DESC = 'Get matched with Burlington, Ontario neighbourhoods that fit your budget, school priorities, and family needs. Free Buyer Blueprint — results in under 3 minutes.';
const LOGO_URL = 'https://buyinburlington.ca/logo.png';

function setMeta(attr, key, content) {
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) { el = document.createElement('meta'); el.setAttribute(attr, key); document.head.appendChild(el); }
  el.setAttribute('content', content);
}

function setLink(rel, href) {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) { el = document.createElement('link'); el.setAttribute('rel', rel); document.head.appendChild(el); }
  el.setAttribute('href', href);
}

function setJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!el) { el = document.createElement('script'); el.id = id; el.type = 'application/ld+json'; document.head.appendChild(el); }
  el.textContent = JSON.stringify(data);
}

function removeJsonLd(id) {
  const el = document.getElementById(id);
  if (el) el.remove();
}

/**
 * SEO Component — handles meta tags + JSON-LD structured data
 * 
 * Props:
 * - title, description, canonical, type, noindex (meta tags)
 * - jsonLd: object or array of JSON-LD schemas to inject
 * - faq: array of {q, a} for FAQ schema
 * - breadcrumbs: array of {name, url} for BreadcrumbList
 * - article: {datePublished, dateModified, author} for Article schema
 */
export default function SEO({ title, description, canonical, type, noindex, jsonLd, faq, breadcrumbs, article }) {
  const t = title ? title + ' | ' + SITE_NAME : SITE_NAME + ' | Find Your Right Neighbourhood in Burlington, Ontario';
  const d = description || DEFAULT_DESC;
  const u = canonical ? BASE_URL + canonical : BASE_URL;
  const ot = type || 'website';

  useEffect(() => {
    // ── Standard Meta Tags ──
    document.title = t;
    setMeta('name', 'description', d);
    setMeta('name', 'robots', noindex ? 'noindex, nofollow' : 'index, follow');
    setLink('canonical', u);

    // Open Graph
    setMeta('property', 'og:type', ot);
    setMeta('property', 'og:title', t);
    setMeta('property', 'og:description', d);
    setMeta('property', 'og:site_name', SITE_NAME);
    setMeta('property', 'og:url', u);
    setMeta('property', 'og:locale', 'en_CA');

    // Twitter
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', t);
    setMeta('name', 'twitter:description', d);

    // Geo
    setMeta('name', 'geo.region', 'CA-ON');
    setMeta('name', 'geo.placename', 'Burlington');

    // ── JSON-LD: Organization (always present) ──
    setJsonLd('ld-org', {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": SITE_NAME,
      "url": BASE_URL,
      "description": "Burlington, Ontario neighbourhood matching and buyer qualification platform.",
      "areaServed": {
        "@type": "City",
        "name": "Burlington",
        "containedInPlace": { "@type": "AdministrativeArea", "name": "Ontario, Canada" }
      }
    });

    // ── JSON-LD: WebSite with SearchAction ──
    setJsonLd('ld-website', {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": SITE_NAME,
      "url": BASE_URL,
      "potentialAction": {
        "@type": "SearchAction",
        "target": { "@type": "EntryPoint", "urlTemplate": BASE_URL + "/guides?q={search_term_string}" },
        "query-input": "required name=search_term_string"
      }
    });

    // ── JSON-LD: BreadcrumbList ──
    if (breadcrumbs && breadcrumbs.length > 0) {
      setJsonLd('ld-breadcrumb', {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": breadcrumbs.map((item, i) => ({
          "@type": "ListItem",
          "position": i + 1,
          "name": item.name,
          "item": BASE_URL + item.url
        }))
      });
    } else {
      removeJsonLd('ld-breadcrumb');
    }

    // ── JSON-LD: FAQ ──
    if (faq && faq.length > 0) {
      setJsonLd('ld-faq', {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faq.map(item => ({
          "@type": "Question",
          "name": item.q,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": item.a
          }
        }))
      });
    } else {
      removeJsonLd('ld-faq');
    }

    // ── JSON-LD: Article ──
    if (article) {
      setJsonLd('ld-article', {
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": title || '',
        "description": d,
        "url": u,
        "publisher": {
          "@type": "Organization",
          "name": SITE_NAME,
          "url": BASE_URL
        },
        "datePublished": article.datePublished || new Date().toISOString().split('T')[0],
        "dateModified": article.dateModified || new Date().toISOString().split('T')[0],
        "author": { "@type": "Organization", "name": SITE_NAME },
        "mainEntityOfPage": { "@type": "WebPage", "@id": u }
      });
    } else {
      removeJsonLd('ld-article');
    }

    // ── JSON-LD: Custom (pass-through) ──
    if (jsonLd) {
      setJsonLd('ld-custom', jsonLd);
    } else {
      removeJsonLd('ld-custom');
    }

    // Cleanup on unmount
    return () => {
      removeJsonLd('ld-breadcrumb');
      removeJsonLd('ld-faq');
      removeJsonLd('ld-article');
      removeJsonLd('ld-custom');
    };
  }, [t, d, u, ot, noindex, jsonLd, faq, breadcrumbs, article, title]);

  return null;
}
